import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Card, CardContent, CardActions, IconButton,
  Grid, TextField, Tooltip, Typography, Menu, MenuItem
} from '@mui/material';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import CloseIcon from '@mui/icons-material/Close';
import {
  DndContext,
  closestCenter,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  arrayMove,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import SwapVertIcon from '@mui/icons-material/SwapVert';

function SortableCard({ p, idx, pages, setPages, moveBefore, onPreview }) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: p.id });
  const [anchorEl, setAnchorEl] = useState(null);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const openMenu = (event) => setAnchorEl(event.currentTarget);
  const closeMenu = () => setAnchorEl(null);

  return (
    <Grid
      item
      xs={12} sm={6} md={4} lg={3}
      ref={setNodeRef}
      style={style}
    >
      <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box
          component="img"
          src={p.largeThumb || p.thumb}
          alt={p.name}
          sx={{ width: '100%', aspectRatio: '3/4', objectFit: 'cover', cursor: 'zoom-in' }}
          onClick={(e) => { e.stopPropagation(); onPreview(p); }}
        />
        <CardContent sx={{ flexGrow: 1 }}>
          <TextField
            label="File name"
            fullWidth
            value={p.name}
            onChange={(e) => {
              const nv = e.target.value.replace(/\s+/g, ' ').trim();
              setPages((arr) => arr.map((x, i) => (i === idx ? { ...x, name: nv } : x)));
            }}
          />
          <Typography variant="caption" color="text.secondary">
            Original page #{p.pageNumber}
          </Typography>
        </CardContent>
        <CardActions>
          <Tooltip title="Drag to reorder">
            <IconButton {...attributes} {...listeners}>
              <DragIndicatorIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Move before…">
            <IconButton color="primary" onClick={openMenu}>
              <SwapVertIcon />
            </IconButton>
          </Tooltip>
          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={closeMenu}>
            {pages
              .filter((x) => x.id !== p.id)
              .map((x, i) => (
                <MenuItem
                  key={x.id}
                  onClick={() => {
                    moveBefore(idx, i);
                    closeMenu();
                  }}
                >
                  Before {x.name}
                </MenuItem>
              ))}
          </Menu>

          <Box sx={{ flexGrow: 1 }} />
          <Tooltip title="Remove">
            <IconButton
              color="error"
              onClick={() => setPages((arr) => arr.filter((_, i) => i !== idx))}
            >
              <DeleteForeverIcon />
            </IconButton>
          </Tooltip>
        </CardActions>
      </Card>
    </Grid>
  );
}

export default function GalleryStep({ pages, setPages }) {
  const [preview, setPreview] = useState(null); // selected page for enlarged view
  const [alignStart, setAlignStart] = useState(false);
  const containerRef = useRef(null);
  const imgRef = useRef(null);

  useEffect(() => {
    if (preview) {
      const prevOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      const onKey = (e) => { if (e.key === 'Escape') setPreview(null); };
      window.addEventListener('keydown', onKey);
      return () => {
        window.removeEventListener('keydown', onKey);
        document.body.style.overflow = prevOverflow;
      };
    }
    return undefined;
  }, [preview]);

  // called when preview image finishes loading
  const onPreviewImageLoad = () => {
    const cont = containerRef.current;
    const img = imgRef.current;
    if (!cont || !img) return;

    // if image is larger than the container in either dimension, align to top-left and reset scroll
    const larger = img.naturalWidth > cont.clientWidth || img.naturalHeight > cont.clientHeight;
    if (larger) {
      setAlignStart(true);
      // ensure scroll starts at top-left
      cont.scrollTo({ top: 0, left: 0 });
    } else {
      setAlignStart(false);
      // center small images
      cont.scrollTo({ top: 0, left: 0 });
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      setPages((arr) => {
        const oldIndex = arr.findIndex((x) => x.id === active.id);
        const newIndex = arr.findIndex((x) => x.id === over.id);
        return arrayMove(arr, oldIndex, newIndex);
      });
    }
  };

  const moveBefore = (fromIdx, targetIdx) => {
    setPages((arr) => {
      const next = arr.slice();
      const [item] = next.splice(fromIdx, 1);
      // recalc new index after removal
      const newTarget = Math.max(0, Math.min(next.length, targetIdx));
      next.splice(newTarget, 0, item);
      return next;
    });
  };

  return (
    <>
      {/* Overlay preview */}
      {preview && (
        <Box
          onClick={() => setPreview(null)}
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 1400,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(0,0,0,0.6)',
            backdropFilter: 'blur(6px)',
            p: 2,
          }}
        >
          <Box
            onClick={(e) => e.stopPropagation()}
            ref={containerRef}
            sx={{
              position: 'relative',
              maxWidth: '96vw',
              maxHeight: '96vh',
              width: 'auto',
              height: 'auto',
              borderRadius: 1,
              boxShadow: 24,
              bgcolor: 'background.paper',
              // make inner area scrollable when image is larger than viewport
              display: 'flex',
              alignItems: alignStart ? 'flex-start' : 'center',
              justifyContent: alignStart ? 'flex-start' : 'center',
              overflow: 'auto',
              p: 1,
            }}
          >
            <IconButton
              onClick={() => setPreview(null)}
              sx={{ position: 'absolute', top: 8, right: 8, zIndex: 10, bgcolor: 'rgba(255,255,255,0.8)' }}
              size="large"
              aria-label="Close preview"
            >
              <CloseIcon />
            </IconButton>
            <Box
              // scroll container centers small images and allows scrolling for large ones
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: 0,
                minHeight: 0,
              }}
            >
              <Box
                component="img"
                ref={imgRef}
                onLoad={onPreviewImageLoad}
                src={preview.largeThumb || preview.thumb}
                alt={preview.name}
                sx={{
                  // render at natural resolution; container will provide scrollbars when needed
                  width: 'auto',
                  height: 'auto',
                  maxWidth: 'none',
                  maxHeight: 'none',
                  display: 'block',
                }}
              />
            </Box>
          </Box>
        </Box>
      )}

      <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={pages.map((p) => p.id)} strategy={rectSortingStrategy}>
          <Grid container spacing={2}>
            {pages.map((p, idx) => (
              <SortableCard
                key={p.id}
                p={p}
                idx={idx}
                pages={pages}
                setPages={setPages}
                moveBefore={moveBefore}
                onPreview={(page) => setPreview(page)}
              />
            ))}
          </Grid>
        </SortableContext>
      </DndContext>
    </>
  );
}
